import { Command } from 'ckeditor5';

/**
 * editor.execute('insertMathEquation', { latex: 'x^2' })              -> inserts new
 * editor.execute('insertMathEquation', { latex: 'x^2', elementToUpdate }) -> edits existing
 *
 * NOTE: "editing" an existing equation does NOT update its `latex`
 * attribute in place. An earlier version tried that plus a downcast
 * `triggerBy` config to force re-rendering, but that reconversion
 * mechanism didn't visibly take effect. Removing the old element and
 * inserting a brand new one at the same spot sidesteps the question
 * entirely -- insertion already reliably triggers a full, fresh render,
 * because it's the exact same code path as a normal first-time insert.
 */
export default class InsertMathEquationCommand extends Command {
  execute({ latex, elementToUpdate } = {}) {
    const editor = this.editor;
    const model = editor.model;

    model.change((writer) => {
      if (elementToUpdate) {
        // Compute an explicit Position and use it IMMEDIATELY (before any
        // removal happens) -- this is the one truly unambiguous way to
        // say "insert here." Two earlier attempts both failed:
        //   1. Computing a position, removing the old element, THEN using
        //      that now-stale position -> "insert-operation-position-invalid"
        //   2. Passing the element itself as the insert target
        //      (writer.insert(replacement, elementToUpdate)) -- this does
        //      NOT mean "insert before elementToUpdate"; it likely nested
        //      the replacement INSIDE elementToUpdate, so removing
        //      elementToUpdate right after wiped out the replacement too,
        //      which is why the equation appeared to vanish entirely.
        const replacement = writer.createElement('mathEquation', { latex });
        const insertPosition = writer.createPositionBefore(elementToUpdate);
        writer.insert(replacement, insertPosition);
        writer.remove(elementToUpdate);
        writer.setSelection(writer.createRangeOn(replacement));
        return;
      }

      const mathEquation = writer.createElement('mathEquation', { latex });
      model.insertObject(mathEquation, null, null, { setSelection: 'after' });
    });
  }

  refresh() {
    const model = this.editor.model;
    const selection = model.document.selection;
    const allowedIn = model.schema.findAllowedParent(
      selection.getFirstPosition(),
      'mathEquation'
    );
    this.isEnabled = allowedIn !== null;
  }
}